'use client';

import { useParams } from 'next/navigation';
import { useMemo } from 'react';
import { useAiFoodOrderStore } from '@/store';

/**
 * Hook để xử lý điều hướng với city_slug dựa trên địa chỉ của user
 * Thay thế các ROUTES cũ để hỗ trợ cấu trúc URL mới: /city_slug/...
 */
export function useCityNavigation() {
  const params = useParams();
  const storeCitySlug = useAiFoodOrderStore.use.city_slug();

  // Lấy city_slug từ URL hiện tại hoặc từ store (đã resolve qua by-point)
  const citySlug = useMemo(() => {
    // Ưu tiên city_slug từ URL hiện tại
    if (params.city && typeof params.city === 'string') {
      return params.city;
    }

    // Nếu không có trong URL, dùng slug đã lưu (from by-point resolution)
    if (storeCitySlug) {
      return storeCitySlug;
    }

    // Fallback về Đà Nẵng nếu không tìm thấy
    return 'da-nang';
  }, [params.city, storeCitySlug]);

  // Các function để tạo URLs với city_slug
  const routes = useMemo(
    () => ({
      // Trang chủ
      home: `/${citySlug}`,
      solomeal: `/${citySlug}/solomeal`,

      // Danh sách cửa hàng (search)
      search: `/${citySlug}/danh-sach-cua-hang`,

      // Chi tiết cửa hàng - Route mới với city_slug
      storeDetail: (storeSlug: string) => `/cua-hang/${storeSlug}`,

      // Order routes cho luồng chính (desktop/mobile web)
      orderStore: (storeId: string) => `/order/${storeId}`,
      orderCart: (storeId: string) => `/order/${storeId}/cart`,

      // Order routes cho webview (luồng /m)
      orderStore_wv: (storeId: string) => `/m/order-wv/${storeId}`,
      orderCart_wv: (storeId: string) => `/m/order-wv/${storeId}/cart`,

      // User routes
      user: '/user',
      orderList: '/order-list',

      // Static routes
      sentryExamplePage: '/sentry-example-page',
      order: '/order',
      store: '/store',
    }),
    [citySlug]
  );

  return {
    citySlug,
    routes,
  };
}

/**
 * Hook đơn giản chỉ để lấy city routes
 * Dùng khi chỉ cần access routes mà không cần logic phức tạp
 */
export function useCityRoutes() {
  const { routes } = useCityNavigation();
  return routes;
}
